import { IconButton, Tooltip } from '@mui/material'
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material'
import { useLocation, useNavigate } from 'react-router-dom'
import { backFallbackPath } from './backNavigation'

/**
 * MWA-362 — an on-screen way back to the previous page, so leaving a page no
 * longer means going through the sidebar (which starts you over at the top).
 *
 * Steps back through history when this tab has an in-app page behind it.
 * React Router numbers its entries in history.state.idx, counting from the
 * first page this tab loaded, so idx 0 means there is nothing of ours behind
 * it. The key cannot answer that: it stops being 'default' after any navigate,
 * including the replace this component does below.
 *
 * With nothing behind it, it goes up to the parent page instead, replacing the
 * entry so repeated presses keep climbing (sub-page, record, list, home) rather
 * than bouncing between two pages or leaving the app. Hidden on the home page
 * when there is nowhere to go. useLocation keeps it re-rendering per entry.
 */
export default function BackButton({ home }: { home: '/' | '/portal' }) {
  const navigate = useNavigate()
  const location = useLocation()
  const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0
  const hasHistory = idx > 0
  const fallback = backFallbackPath(location.pathname, home)

  if (!hasHistory && !fallback) return null

  return (
    <Tooltip title="Back">
      <IconButton
        color="inherit"
        aria-label="Go back to the previous page"
        onClick={() => (hasHistory ? navigate(-1) : navigate(fallback as string, { replace: true }))}
        sx={{ mr: { xs: 0.5, sm: 1 } }}
      >
        <ArrowBackIcon />
      </IconButton>
    </Tooltip>
  )
}
